import React from "react";
import "./AccreditationPage.css";

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

const mqfLevels = [
  { level: "8", title: "Doctoral Degree", examples: "PhD, EdD", current: false },
  { level: "7", title: "Master\u2019s Degree", examples: "MA, MSc, MBA", current: true },
  { level: "6", title: "Bachelor\u2019s Degree", examples: "BA, BSc", current: false },
  { level: "5", title: "Foundation / HND", examples: "Certificates, Diplomas", current: false },
];

const euroRecognition = [
  {
    title: "European Qualifications Framework (EQF)",
    text: "The MQF is directly referenced to the EQF, meaning ESBD qualifications at MQF Level 7 correspond to EQF Level 7 across all EU member states. This enables transparent comparison and recognition of your qualification throughout Europe.",
  },
  {
    title: "Bologna Process",
    text: "Malta is one of 48 member countries participating in the Bologna Process, which harmonises higher education qualifications across Europe. ESBD qualifications are structured in line with the three-cycle degree system (Bachelor, Master, Doctorate) and use the European Credit Transfer System (ECTS).",
  },
  {
    title: "ENIC-NARIC Network",
    text: "The Malta Qualifications Recognition Information Centre (MQRIC), housed within MFHEA, is Malta\u2019s official ENIC-NARIC centre. This network facilitates the recognition of academic qualifications across borders, enabling ESBD graduates to have their credentials formally assessed and recognised in over 55 countries.",
  },
  {
    title: "QF-EHEA Alignment",
    text: "The MQF is referenced to the Qualifications Framework of the European Higher Education Area, ensuring ESBD qualifications are compatible with the higher education systems of all Bologna Process signatory countries.",
  },
];

const studentBenefits = [
  { title: "Recognised Across the EU & Internationally", text: "Upon programme accreditation, your MQF Level 7 qualification will be referenced to EQF Level 7 and recognised across all EU and EEA member states, as well as internationally through the ENIC-NARIC network and established mutual recognition frameworks spanning over 55 countries worldwide." },
  { title: "Employer Confidence", text: "Upon accreditation, qualifications from an MFHEA-regulated institution will signal to employers that your education is delivered within a framework of rigorous, independently verified quality standards set by a national regulatory authority." },
  { title: "Pathway to Further Study", text: "An MQF Level 7 qualification provides a recognised foundation for progression to doctoral study (MQF Level 8) at institutions across Europe and beyond." },
  { title: "Credential Verification", text: "Upon programme accreditation, graduates will be able to obtain official recognition statements through MQRIC, Malta\u2019s ENIC-NARIC centre, providing formal verification of their qualification for employers, immigration authorities, and other institutions worldwide." },
  { title: "Transparent Credit System", text: "ESBD uses the European Credit Transfer and Accumulation System (ECTS), enabling credit transfer and mobility between institutions across the European Higher Education Area." },
  { title: "Quality-Assured Education", text: "MFHEA\u2019s accreditation process requires external peer review against 9 mandatory quality standards aligned with the European Standards and Guidelines (ESG) for quality assurance in higher education." },
];

const qaStandards = [
  "Curriculum Design & Programme Structure",
  "Teaching, Learning & Assessment Methods",
  "Student Support & Resources",
  "Academic Staff Qualifications & Development",
  "Governance & Internal Quality Assurance",
  "Learning Outcomes & Graduate Competences",
  "Information Management & Public Reporting",
  "Monitoring, Review & Continuous Improvement",
  "External Quality Assurance & Compliance",
];

const regions = [
  { title: "Europe", text: "Directly recognised through the EQF, Bologna Process, and ENIC-NARIC network across all 27 EU member states, EEA countries, and 48 Bologna signatory nations." },
  { title: "Africa", text: "Recognised through the Transnational Qualifications Framework (TQF) of the Commonwealth and bilateral recognition agreements. ESBD actively supports African students through dedicated scholarship programmes." },
  { title: "Middle East & Asia", text: "Credential verification available through MQRIC and the global ENIC-NARIC network. Many countries in the region have established NQFs referenced to the EQF, facilitating mutual recognition." },
  { title: "United Kingdom", text: "Malta\u2019s MQF is aligned with the Framework for Higher Education Qualifications (FHEQ) in England, Wales, and Northern Ireland through shared referencing to the QF-EHEA. MQF Level 7 corresponds to FHEQ Level 7." },
];

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 12L12 4M12 4H6M12 4v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Accreditation() {
  return (
    <div className="accreditation-page">
      {/* HERO */}
      <section className="hero">
        <div
          className="hero__bg"
          style={{
            backgroundImage: "url(/images/bg/accreditation-hero-bg.jpeg)",
          }}
        />
        <div className="hero__ov" />
        <div className="hero__c">
          <h1 className="hero__t">Accreditation &amp; International Recognition</h1>
          <p className="hero__s">Quality, credibility, and global recognition through Malta's national regulatory framework</p>
        </div>
      </section>

      {/* ACCREDITATION STATUS */}
      <div className="s">
        <div className="g2">
          <div>
            <div className="badge badge--n">Institutional Licensing: In Progress</div>
            <div className="badge badge--n" style={{ marginLeft: 0, display: "inline-flex" }}>
              Programme Accreditation: Final Stage
            </div>
            <br />
            <div className="sh">
              <h2>Our Accreditation Status</h2>
            </div>
            <div className="txt">
              <p>
                The European School of Business &amp; Diplomacy (ESBD) is a Malta-based higher education institution currently in the final
                stages of obtaining its institutional licence and programme accreditation from the Malta Further &amp; Higher Education Authority
                (MFHEA). Upon completion, ESBD will be authorised to deliver higher education programmes aligned with the Malta Qualifications
                Framework (MQF).
              </p>
              <p>
                Our flagship programme, the MA in Global Business &amp; Strategic Leadership, has been designed to MQF Level 7, 90 ECTS
                standards. The Self-Assessment Report has been completed across all required domains, and we are progressing through MFHEA's
                rigorous independent evaluation process, which includes external peer review to confirm our programme meets established national
                and international quality standards.
              </p>
            </div>
          </div>
          <div>
            <div className="st-grid">
              <div className="st">
                <div className="st__num">MFHEA</div>
                <div className="st__label">Regulatory Authority</div>
              </div>
              <div className="st">
                <div className="st__num">MQF 7</div>
                <div className="st__label">Qualification Level</div>
              </div>
              <div className="st">
                <div className="st__num">90</div>
                <div className="st__label">ECTS Credits</div>
              </div>
              <div className="st">
                <div className="st__num">EU</div>
                <div className="st__label">EQF Referenced</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MQF */}
      <div className="s s--f s--a" style={{ padding: "64px 0" }}>
        <div className="si">
          <div className="g2">
            <div>
              <div className="sh">
                <h2>The Malta Qualifications Framework</h2>
              </div>
              <div className="txt">
                <p>
                  The Malta Qualifications Framework (MQF) is Malta's national framework for classifying qualifications across 8 levels, from
                  introductory certificates (Level 1) through to doctoral degrees (Level 8). Established in 2007, the MQF is maintained and
                  regulated by the MFHEA.
                </p>
                <p>
                  The MQF is formally referenced to the European Qualifications Framework (EQF) and the Qualifications Framework of the European
                  Higher Education Area (QF/EHEA). Malta was the first country recognised by the Council of Europe to reference its framework to two
                  European overarching frameworks and the Transnational Qualifications Framework (TQF) of the Commonwealth.
                </p>
              </div>
            </div>
            <div>
              <div className="mqf">
                {mqfLevels.map((l, i) => (
                  <div key={i} className={`mqf-l${l.current ? " mqf-l--cur" : ""}`}>
                    <div className="mqf-n">{l.level}</div>
                    <div>
                      <div className="mqf-t">{l.title}</div>
                      <div className="mqf-x">{l.examples}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: "var(--text-body)", marginTop: 12, fontStyle: "italic" }}>
                ESBD's MA programme sits at MQF/EQF Level 7 — Master's Degree level
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* EUROPEAN & INTERNATIONAL RECOGNITION */}
      <div className="s s--f s--n" style={{ padding: "64px 0" }}>
        <div className="si">
          <div className="sh" style={{ textAlign: "center" }}>
            <h2>European &amp; International Recognition</h2>
            <p>How the Malta Qualifications Framework connects to the world</p>
          </div>
          <div className="eu-grid">
            {euroRecognition.map((e, i) => (
              <div key={i} className="eu">
                <h3>{e.title}</h3>
                <p>{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WHAT THIS MEANS FOR YOU */}
      <div className="s">
        <div className="sh" style={{ textAlign: "center" }}>
          <h2>What This Means for You</h2>
          <p>The tangible benefits of studying at an MFHEA-regulated institution</p>
        </div>
        <div className="bf-grid">
          {studentBenefits.map((b, i) => (
            <div key={i} className="bf">
              <div className="bf__dot" />
              <h4>{b.title}</h4>
              <p>{b.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* QUALITY ASSURANCE */}
      <div className="s s--f s--n" style={{ padding: "64px 0" }}>
        <div className="si">
          <div className="sh" style={{ textAlign: "center" }}>
            <h2>Quality Assurance Standards</h2>
            <p>MFHEA's accreditation framework is built on 9 mandatory standards aligned with the European Standards and Guidelines (ESG)</p>
          </div>
          <div className="qa-grid">
            {qaStandards.map((s, i) => (
              <div key={i} className="qa">
                <div className="qa__n">{i + 1}</div>
                <p>{s}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", textAlign: "center", marginTop: 20 }}>
            All accredited programmes undergo external peer review and are subject to re-accreditation every five years
          </p>
        </div>
      </div>

      {/* RECOGNITION BY REGION */}
      <div className="s">
        <div className="sh" style={{ textAlign: "center" }}>
          <h2>Recognition by Region</h2>
          <p>How your ESBD qualification is recognised around the world</p>
        </div>
        <div className="rg-grid">
          {regions.map((r, i) => (
            <div key={i} className="rg">
              <h4>{r.title}</h4>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SAMPLE EXPLANATORY LETTER */}
      <div className="s">
        <div className="sh" style={{ textAlign: "center" }}>
          <h2>Sample Explanatory Letter</h2>
          <p>
            Upon request, ESBD issues official explanatory letters to attest to the quality and recognition of qualifications awarded, in
            addition to certificates and transcripts
          </p>
        </div>
        <div
          style={{
            background: "var(--white)",
            border: "1px solid #ECECEA",
            borderRadius: 16,
            padding: "40px 48px",
            maxWidth: 860,
            margin: "0 auto",
            boxShadow: "0 4px 16px rgba(12,31,63,0.04)",
          }}
        >
          <p style={{ fontSize: 13, color: "var(--text-body)", marginBottom: 24 }}>To whom it may concern</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", marginBottom: 16 }}>
            Re: [Student Name] — Recognition of ESBD Qualification
          </p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            [Student Name] was awarded [qualification title] by the European School of Business &amp; Diplomacy (ESBD) on [date]. We have been
            contacted by [Student Name] to attest to their qualification. This letter sets out ESBD's authority to award higher education
            qualifications in Malta and how the academic standards of its qualifications are assured.
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>The European School of Business &amp; Diplomacy</p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            The European School of Business &amp; Diplomacy (ESBD) is a licensed Higher Education Institution in Malta, Europe, operating under
            the regulatory oversight of the Malta Further &amp; Higher Education Authority (MFHEA). ESBD delivers accredited postgraduate
            programmes in global business, strategic leadership, and diplomacy through its fully online learning environment, serving students
            across Europe, Africa, Asia, and the Middle East.
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>
            Authority to Award Higher Education Qualifications
          </p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            ESBD's programmes are accredited by the Malta Further &amp; Higher Education Authority (MFHEA), the competent national authority
            responsible for licensing higher education institutions and accrediting programmes against the Malta Qualifications Framework (MQF).
            The MFHEA maintains a public register of all licensed providers and accredited programmes, which can be verified at{" "}
            <span style={{ color: "var(--navy)", fontWeight: 500 }}>mfhea.mt</span>. ESBD's degree programmes are pegged to the Malta
            Qualifications Framework and are comparable with qualifications awarded by other accredited higher education institutions in Malta and
            across the European Union.
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>
            International Recognition &amp; Quality Assurance
          </p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            The Malta Qualifications Framework (MQF) is formally referenced to the European Qualifications Framework (EQF) and the Qualifications
            Framework of the European Higher Education Area (QF-EHEA). Malta is a signatory to the Bologna Process and a member of the ENIC-NARIC
            network, which facilitates the recognition of academic qualifications across more than 55 countries worldwide. ESBD's academic standards are assured through MFHEA's accreditation process, which is aligned with the European Standards and Guidelines (ESG) for quality assurance in higher education. This process includes independent external peer review, ongoing monitoring, and periodic re-accreditation.
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Quality of Higher Education Qualifications</p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            The qualification referenced above has been awarded following successful completion of an accredited programme of study that meets
            the learning outcomes, credit requirements, and level descriptors prescribed by the MQF at Level 7 (Master's Degree). The programme has been independently evaluated and accredited by the MFHEA, confirming that it meets established national and international quality standards.
          </p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 16 }}>
            We trust that the above information is sufficient to attest to the quality credentials of the European School of Business &amp; Diplomacy and the qualifications it awards, as well as its authority to confer degrees that are recognised under the Malta Qualifications Framework and the European Qualifications Framework.
          </p>
          <p style={{ fontSize: 13.5, lineHeight: 1.75, color: "var(--text-body)", marginBottom: 24 }}>
            Please do not hesitate to contact us should you require any further information.
          </p>
          <div style={{ borderTop: "1px solid #F0F0EC", paddingTop: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Head of Institution</p>
            <p style={{ fontSize: 12.5, color: "var(--text-body)" }}>European School of Business &amp; Diplomacy</p>
            <p style={{ fontSize: 12.5, color: "var(--text-body)" }}>341 Tower Business Centre, 2nd Floor, Triq it-Torri Swatar</p>
            <p style={{ fontSize: 12.5, color: "var(--text-body)" }}>Birkirkara BKR 4013, Malta, Europe</p>
            <p style={{ fontSize: 12.5, color: "var(--text-body)", marginTop: 4 }}>info@eusbd.eu | eusbd.eu</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta">
        <h2>Study with Confidence</h2>
        <p>Join an institution committed to achieving full MFHEA accreditation and international recognition.</p>
        <a href={MA_APPLY_URL} target="_blank" rel="noreferrer">
          Apply Now <ArrowIcon />
        </a>
      </div>
    </div>
  );
}

